import { config } from '../config'

/**
 * Gets context priming information from the first frame
 * @param {string} frame - Base64 encoded first frame image
 * @returns {Promise<string>} - Context description from LLM
 */
export async function getContextPriming(frame) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout for context priming

  try {
    const requestBody = {
      frames: [frame],
      contextPriming: true,
    }

    console.log('Requesting context priming analysis...')

    const response = await fetch(config.api.analyzeEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Context Priming API Error Response:', errorData)
      const errorMessage = errorData.details 
        ? `${errorData.message}\n\nDetails: ${errorData.details}`
        : errorData.message || `Context priming failed with status ${response.status}`
      throw new Error(errorMessage)
    }

    const result = await response.json()
    console.log('Context Priming Success Response:', result)
    return result.context
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Context priming request timed out.')
    }
    throw error
  }
}

/**
 * Analyzes video frames using the Cloudflare Worker API
 * @param {Array<string>} frames - Array of base64 encoded image frames
 * @param {string} filename - Original filename
 * @param {string|null} poseAnalysis - Optional pose analysis data to include in prompt
 * @param {string|null} customPrompt - Optional custom user instructions to prepend to the prompt
 * @param {string|null} contextInfo - Optional context info from context priming
 * @returns {Promise<Object>} - Analysis result
 */
export async function analyzeVideo(frames, filename, poseAnalysis = null, customPrompt = null, contextInfo = null) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), config.api.timeout)

  try {
    const requestBody = {
      frames,
      filename,
    }
    
    // Include pose analysis if provided
    if (poseAnalysis) {
      requestBody.poseAnalysis = poseAnalysis
      console.log('Including pose analysis data in request')
    }
    
    // Include custom prompt if provided
    if (customPrompt && customPrompt.trim()) {
      requestBody.customPrompt = customPrompt.trim()
      console.log('Including custom user prompt in request')
    }
    
    // Include context info if provided
    if (contextInfo && contextInfo.trim()) {
      requestBody.contextInfo = contextInfo.trim()
      console.log('Including context priming info in request')
    }

    const response = await fetch(config.api.analyzeEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error Response:', errorData)
      const errorMessage = errorData.details 
        ? `${errorData.message}\n\nDetails: ${errorData.details}`
        : errorData.message || `Analysis failed with status ${response.status}`
      throw new Error(errorMessage)
    }

    const result = await response.json()
    console.log('API Success Response:', result)
    return result
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again with a shorter video.')
    }
    throw error
  }
}

/**
 * Health check for the API
 * @returns {Promise<boolean>} - True if API is healthy
 */
export async function healthCheck() {
  try {
    const response = await fetch('/api/health', {
      method: 'GET',
    })
    return response.ok
  } catch {
    return false
  }
}

